import { useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SupportFeedbackFormProps {
  onSuccess: () => void;
}

const categories = [
  { value: "bug", label: "🐛 Bug / Problema" },
  { value: "suggestion", label: "💡 Sugestão" },
  { value: "praise", label: "⭐ Elogio" },
];

export function SupportFeedbackForm({ onSuccess }: SupportFeedbackFormProps) {
  const [feedbackType, setFeedbackType] = useState<string>("");
  const [text, setText] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!text.trim() || !feedbackType) return;
    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Você precisa estar logado para enviar feedback.");
        return;
      }

      await supabase.from("support_conversations").insert({
        user_id: user.id,
        category: "feedback",
        feedback_text: `[${feedbackType}] ${text.trim()}`,
        feedback_rating: rating || null,
        messages: [{ role: "user", content: text.trim() }] as any,
        status: "open",
      });

      toast.success("Feedback enviado com sucesso! Obrigado! 🎉");
      onSuccess();
    } catch {
      toast.error("Erro ao enviar feedback. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex-1 p-4 overflow-y-auto space-y-4">
      <p className="text-sm text-muted-foreground">
        Sua opinião é muito importante para nós! 💜
      </p>

      {/* Type selection */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Tipo</label>
        <div className="flex gap-2">
          {categories.map(cat => (
            <button
              key={cat.value}
              onClick={() => setFeedbackType(cat.value)}
              className={`flex-1 py-2 px-3 rounded-lg border text-xs font-medium transition-colors ${
                feedbackType === cat.value
                  ? "bg-primary text-primary-foreground border-primary"
                  : "hover:bg-accent/50"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Rating */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">
          Como avalia a plataforma? (opcional)
        </label>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map(star => (
            <button
              key={star}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              className="p-1"
            >
              <Star
                className={`w-6 h-6 transition-colors ${
                  star <= (hoverRating || rating)
                    ? "fill-primary text-primary"
                    : "text-muted-foreground/30"
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Text */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">Sua mensagem</label>
        <Textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Descreva seu feedback em detalhes..."
          className="min-h-[100px] text-sm"
          maxLength={2000}
        />
        <span className="text-xs text-muted-foreground">{text.length}/2000</span>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={!text.trim() || !feedbackType || isSubmitting}
        className="w-full"
      >
        {isSubmitting ? "Enviando..." : "Enviar Feedback"}
      </Button>
    </div>
  );
}
